import { styled } from '@mui/material/styles';
import Icon from './Icon';
import { TAG_COLORS } from '../shared/icons.generated';
import { BRAND } from '../shared/theme/paletteDark';
import { FONTS } from '../shared/theme/typography';

/**
 * One topic tag, drawn exactly once for the whole app.
 *
 * Follows "TAG ICON REFERENCE - dark+light mode enabled+disabled": every pill is
 * the same height whether its label runs to one line or two, the icon sits left
 * at a fixed size, and the two states are
 *
 *   enabled   pastel fill, mocha icon and text
 *   disabled  tinted dark fill, pastel icon and text, pastel border
 *
 * Both the sidebar filters and the sticky notes on an event card render through
 * this component, which is what keeps them identical. Color and artwork are
 * keyed by the topic's key, the label is only what it says.
 */

/** The icon's size on a phone. */
const ICON_SIZE = { sm: 13, md: 17 } as const;

const Pill = styled('span', {
  shouldForwardProp: (p) => p !== '$color' && p !== '$on' && p !== '$size' && p !== '$stuck',
})<{ $color: string; $on: boolean; $size: 'sm' | 'md'; $stuck: boolean }>(({
  $color,
  $on,
  $size,
  $stuck,
}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: $size === 'sm' ? 5 : 7,
  width: '100%',
  minHeight: $size === 'sm' ? 30 : 40,
  padding: $size === 'sm' ? '4px 8px' : '6px 10px',
  // "Stuck" notes lose their right-hand radius so they read as paper adhered to
  // the card edge rather than a floating chip.
  borderRadius: $stuck ? '7px 0 0 7px' : 7,
  borderRight: $stuck ? 'none' : undefined,
  boxSizing: 'border-box',
  textAlign: 'left',
  fontFamily: FONTS.body,
  fontSize: $size === 'sm' ? 10 : 11.5,
  fontWeight: 700,
  lineHeight: 1.16,
  letterSpacing: '0.005em',
  // `color` drives the icon too, via currentColor.
  color: $on ? BRAND.mocha : $color,
  background: $on ? $color : `color-mix(in srgb, ${$color} 13%, ${BRAND.mochaDeep})`,
  border: `1px solid ${$on ? $color : `color-mix(in srgb, ${$color} 42%, transparent)`}`,
  transition: 'background 120ms ease, color 120ms ease, border-color 120ms ease',
  // Half as large again past the phone layout, where the pills have room for it.
  '@media (min-width: 600px)': {
    '& > svg': { width: ICON_SIZE[$size] * 1.5, height: ICON_SIZE[$size] * 1.5 },
  },
}));

const Label = styled('span')({
  minWidth: 0,
  flex: 1,
});

/** A filter's count, at the pill's end. */
const Count = styled('span')({
  flexShrink: 0,
  fontSize: 10,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  opacity: 0.7,
});

interface TagPillProps {
  topic: { key: string; label: string; icon?: string | null };
  /** Enabled state: pastel fill with dark text. */
  on?: boolean;
  size?: 'sm' | 'md';
  /** Square off the right edge, for notes butted against a card. */
  stuck?: boolean;
  /** In the filter sidebar: how many events choosing it would show. */
  count?: number;
  className?: string;
}

export default function TagPill({
  topic,
  on = false,
  size = 'md',
  stuck = false,
  count,
  className,
}: TagPillProps) {
  const color = TAG_COLORS[`topic:${topic.key}`] ?? BRAND.muted;
  return (
    <Pill $color={color} $on={on} $size={size} $stuck={stuck} className={className}>
      <Icon name={`topic:${topic.key}`} emoji={topic.icon} size={ICON_SIZE[size]} />
      <Label>{topic.label}</Label>
      {count != null && <Count aria-hidden="true">{count}</Count>}
    </Pill>
  );
}
